/**
 * Cart Drawer - Main sliding cart panel
 * Uses Shadcn Sheet component, follows Front Desk design
 */
import { useState } from 'react';
import { ShoppingCart, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useCart } from '@/contexts/CartContext';
import { CartItemCard } from './CartItemCard';
import { CartSummaryCard } from './CartSummaryCard';
import { CartEmptyState } from './CartEmptyState';
import { CartLoadingState } from './CartLoadingState';
import { CheckoutDialog } from './CheckoutDialog';
import { toast } from 'sonner';

export function CartDrawer({ open, onOpenChange }) {
  const { cart, clearCart } = useCart();
  const [showCheckout, setShowCheckout] = useState(false);
  const items = cart.items || [];
  const isEmpty = items.length === 0;
  const isLoading = cart.loading;

  const handleClearCart = async () => {
    if (window.confirm('Remove all items from cart?')) {
      await clearCart();
    }
  };

  const handleCheckout = () => {
    setShowCheckout(true);
  };

  const handleCheckoutSuccess = (data) => {
    // Close both dialogs
    setShowCheckout(false);
    onOpenChange(false);
    
    // Show success message
    toast.success('🎉 Bookings Confirmed!', {
      description: `Successfully created ${data.bookings.total} booking(s). Check your email for confirmation.`,
      duration: 5000
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col p-0"
        data-testid="cart-drawer"
      >
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <SheetTitle className="heading-font text-xl">
                  Your Cart
                </SheetTitle>
                <SheetDescription className="text-xs">
                  {isEmpty ? 'No items yet' : `${items.length} ${items.length === 1 ? 'item' : 'items'}`}
                </SheetDescription>
              </div>
            </div>
            {!isEmpty && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearCart}
                className="text-xs text-muted-foreground hover:text-destructive"
                data-testid="cart-clear-all-button"
              >
                Clear all
              </Button>
            )}
          </div>
        </SheetHeader>

        {/* Content */}
        {isLoading ? (
          <CartLoadingState />
        ) : isEmpty ? (
          <CartEmptyState onClose={() => onOpenChange(false)} />
        ) : (
          <>
            {/* Items List */}
            <ScrollArea className="flex-1 px-6 py-4">
              <div className="space-y-3" data-testid="cart-items-list">
                {items.map((item) => (
                  <CartItemCard key={item.id} item={item} />
                ))}
              </div>
            </ScrollArea>

            {/* Footer with Summary & Checkout */}
            <div className="border-t bg-card px-6 py-4 space-y-4">
              <CartSummaryCard pricing={cart.pricing} />
              
              <Button
                size="lg"
                className="w-full text-base"
                onClick={handleCheckout}
                data-testid="cart-checkout-button"
              >
                Proceed to Checkout
                <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
              </Button>
              
              <p className="text-xs text-center text-muted-foreground">
                Secure checkout powered by Emergent Payments
              </p>
            </div>
          </>
        )}
      </SheetContent>
      
      {/* Checkout Dialog */}
      <CheckoutDialog 
        open={showCheckout} 
        onClose={() => setShowCheckout(false)}
        onSuccess={handleCheckoutSuccess}
      />
    </Sheet>
  );
}
